import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';
import { AddonGrant } from '@/types/addons';
import { usePageVisibility } from './usePageVisibility';

export type AddonType = string; // Changed from enum to string to support dynamic addons

interface BrandAddon extends AddonGrant {}

export const useBrandAddons = () => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const [addons, setAddons] = useState<BrandAddon[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserAddons = async () => {
    if (!user || !currentBrandId) {
      setAddons([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_brand_addons')
        .select('id,user_id,brand_id,addon,granted_by,granted_at,created_at,updated_at')
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId);

      if (error) {
        console.error('Error fetching user addons:', error);
        setAddons([]);
      } else {
        setAddons((data as any) || []);
      }
    } catch (error) {
      console.error('Error fetching user addons:', error);
      setAddons([]);
    } finally {
      setIsLoading(false);
    }
  };

  const hasAddonAccess = (addonType: AddonType): boolean => {
    return addons.some(addon => addon.addon === addonType);
  };

  const checkAddonAccess = async (addonType: AddonType): Promise<boolean> => {
    if (!user || !currentBrandId) return false;
    try {
      const { count, error } = await supabase
        .from('user_brand_addons')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId)
        .eq('addon', addonType);
      if (error) return false;
      return (count || 0) > 0;
    } catch {
      return false;
    }
  };
  const grantAddonAccess = async (
    targetUserId: string,
    brandId: string,
    addonType: AddonType
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_brand_addons')
        .insert({
          user_id: targetUserId,
          brand_id: brandId,
          addon: addonType,
          granted_by: user.id,
        });

      if (error) {
        console.error('Error granting addon access:', error);
        return false;
      }

      // Refresh addons if it's for current user/brand
      if (targetUserId === user.id && brandId === currentBrandId) {
        await fetchUserAddons();
      }

      return true;
    } catch (error) {
      console.error('Error granting addon access:', error);
      return false;
    }
  };

  const revokeAddonAccess = async (
    targetUserId: string,
    brandId: string,
    addonType: AddonType
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('user_brand_addons')
        .delete()
        .eq('user_id', targetUserId)
        .eq('brand_id', brandId)
        .eq('addon', addonType);

      if (error) {
        console.error('Error revoking addon access:', error);
        return false;
      }

      // Refresh addons if it's for current user/brand
      if (targetUserId === user?.id && brandId === currentBrandId) {
        await fetchUserAddons();
      }

      return true;
    } catch (error) {
      console.error('Error revoking addon access:', error);
      return false;
    }
  };

  const { isVisible } = usePageVisibility();

  useEffect(() => {
    fetchUserAddons();
  }, [user, currentBrandId]);

  // Set up real-time subscription (visibility-aware)
  useEffect(() => {
    if (!user || !currentBrandId || !isVisible) return;

    const channel = supabase
      .channel(`user_brand_addons_changes-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_brand_addons',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchUserAddons();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentBrandId, isVisible]);

  return {
    addons,
    isLoading,
    hasAddonAccess,
    grantAddonAccess,
    revokeAddonAccess,
    refetch: fetchUserAddons,
  };
};